/**
 * Portfolio Component Inventory Service
 * Aggregates component data across multiple sites for portfolio-level views
 *
 * NOTE: This service currently builds data from existing site entity endpoints.
 * When portfolio-level backend endpoints are available, swap out the data fetching
 * logic in aggregatePortfolioComponents().
 */

import type { Entity, EntityConnection } from './apiService';
import type { PortfolioSite } from '../redux/api/portfolioApi';
import { ComponentColors } from '../components/graph-viz/types';

// ============================================================================
// Types - Following the PRD data model
// ============================================================================

export interface SiteComponentCount {
  site_id: string;
  site_name: string;
  site_location: { lat: number; lng: number } | null;
  count: number;
  cod_date: string | null;
  component_age_days: number | null;
}

export interface AgeBucket {
  label: string;
  minDays: number;
  maxDays: number;
  count: number;
}

export interface AgeDistribution {
  average_age_days: number;
  min_age_days: number;
  max_age_days: number;
  buckets: AgeBucket[];
}

export interface ComponentModelSummary {
  manufacturer: string;
  model: string;
  normalized_id: string;
  total_count: number;
  site_distribution: SiteComponentCount[];
  specifications: Record<string, any>;
  average_age_years: number | null;
}

export interface PortfolioComponentSummary {
  component_category: string;
  display_name: string;
  total_count: number;
  site_count: number;
  unique_model_count: number;
  models: ComponentModelSummary[];
  age_distribution: AgeDistribution | null;
  color: string;
}

export interface PortfolioComponentData {
  summaries: PortfolioComponentSummary[];
  total_components: number;
  total_sites: number;
  last_updated: string;
}

export interface SelectedSiteData {
  site: PortfolioSite;
  entities: Entity[];
  connections: EntityConnection[];
}

// ============================================================================
// Component Category Mapping
// ============================================================================

// Map raw component types to user-friendly categories
const COMPONENT_CATEGORY_MAP: Record<string, { category: string; displayName: string }> = {
  // Power Conversion
  inverter: { category: 'inverter', displayName: 'Inverters' },
  pcs_bidirectional: { category: 'pcs', displayName: 'Power Conversion Systems' },
  pcs_skid: { category: 'pcs', displayName: 'Power Conversion Systems' },
  pcs_tap: { category: 'pcs', displayName: 'Power Conversion Systems' },
  microinverter: { category: 'microinverter', displayName: 'Microinverters' },

  // DC Collection
  combiner_box: { category: 'combiner', displayName: 'Combiner Boxes' },
  mppt: { category: 'mppt', displayName: 'MPPT Controllers' },
  dc_optimizer: { category: 'optimizer', displayName: 'DC Optimizers' },

  // Modules
  pv_module: { category: 'module', displayName: 'PV Modules' },
  module: { category: 'module', displayName: 'PV Modules' },
  solar_module: { category: 'module', displayName: 'PV Modules' },
  solar_panel: { category: 'module', displayName: 'PV Modules' },
  panel: { category: 'module', displayName: 'PV Modules' },
  photovoltaic_module: { category: 'module', displayName: 'PV Modules' },

  // Strings
  string: { category: 'string', displayName: 'Strings' },

  // Protection
  fuse: { category: 'fuse', displayName: 'Fuses' },
  circuit_breaker: { category: 'breaker', displayName: 'Circuit Breakers' },
  disconnect_switch: { category: 'disconnect', displayName: 'Disconnect Switches' },
  rapid_shutdown_device: { category: 'rsd', displayName: 'Rapid Shutdown Devices' },
  protective_relay: { category: 'relay', displayName: 'Protective Relays' },
  surge_protection_device: { category: 'spd', displayName: 'Surge Protection' },

  // Transformers
  transformer: { category: 'transformer', displayName: 'Transformers' },
  voltage_transformer: { category: 'transformer', displayName: 'Transformers' },
  current_transformer: { category: 'transformer', displayName: 'Transformers' },
  control_transformer: { category: 'transformer', displayName: 'Transformers' },

  // Switchgear
  switchgear_mv: { category: 'switchgear', displayName: 'MV Switchgear' },
  switchboard_lv: { category: 'switchboard', displayName: 'LV Switchboards' },
  panelboard: { category: 'panelboard', displayName: 'Panelboards' },

  // Monitoring & Communications
  scada: { category: 'scada', displayName: 'SCADA Systems' },
  gateway: { category: 'gateway', displayName: 'Gateways' },
  rtu: { category: 'rtu', displayName: 'RTUs' },
  plc: { category: 'plc', displayName: 'PLCs' },
  data_logger: { category: 'datalogger', displayName: 'Data Loggers' },
  das: { category: 'das', displayName: 'DAS' },

  // Metering
  revenue_meter: { category: 'meter', displayName: 'Revenue Meters' },
  power_meter: { category: 'meter', displayName: 'Power Meters' },
  meter: { category: 'meter', displayName: 'Meters' },

  // Weather & Sensors
  weather_station: { category: 'weather', displayName: 'Weather Stations' },
  pyranometer: { category: 'sensor', displayName: 'Irradiance Sensors' },
  ambient_temp_sensor: { category: 'sensor', displayName: 'Temperature Sensors' },
  module_temp_sensor: { category: 'sensor', displayName: 'Module Temp Sensors' },
  anemometer: { category: 'sensor', displayName: 'Anemometers' },

  // Tracking
  tracker_controller: { category: 'tracker', displayName: 'Tracker Controllers' },
  racking_system: { category: 'racking', displayName: 'Racking Systems' },

  // Grid
  grid_connection_point: { category: 'grid', displayName: 'Grid Connection Points' },
  grid: { category: 'grid', displayName: 'Grid' },
};

// Priority order for displaying categories (higher priority = shown first)
const CATEGORY_PRIORITY: Record<string, number> = {
  inverter: 100,
  module: 95,
  combiner: 90,
  string: 85,
  transformer: 80,
  fuse: 75,
  breaker: 70,
  meter: 65,
  switchgear: 60,
  tracker: 55,
  scada: 50,
  weather: 45,
  sensor: 40,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get category info for a component type
 */
export function getCategoryInfo(componentType: string): { category: string; displayName: string } {
  const type = componentType?.toLowerCase() || 'unknown';
  return COMPONENT_CATEGORY_MAP[type] || { category: type, displayName: formatDisplayName(type) };
}

/**
 * Check if entity has modules (for filtering purposes)
 * Returns true if:
 * - Entity is directly a module category
 * - Entity is a string with embedded module info
 */
export function entityHasModules(entity: any): boolean {
  const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || '';
  const { category } = getCategoryInfo(componentType);

  if (category === 'module') return true;

  // Check for modules embedded in string entities
  if (componentType.toLowerCase() === 'string') {
    const config = entity.configuration as Record<string, any> | undefined;
    if (config?.aggregate_specs?.module_properties?.Model || config?.aggregate_specs?.module_properties?.model) {
      return true;
    }
  }

  return false;
}

/**
 * Get module count from an entity (either direct module or from string)
 */
export function getModuleCountFromEntity(entity: any): number {
  const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || '';
  const { category } = getCategoryInfo(componentType);

  if (category === 'module') return 1;

  // Check for modules embedded in string entities
  if (componentType.toLowerCase() === 'string') {
    const config = entity.configuration as Record<string, any> | undefined;
    if (config?.aggregate_specs?.module_properties?.Model || config?.aggregate_specs?.module_properties?.model) {
      return config.aggregate_specs.module_count || 1;
    }
  }

  return 0;
}

/**
 * Format a component type string to a display name
 */
function formatDisplayName(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') + 's';
}

/**
 * Parse site location to coordinates
 */
export function parseSiteLocation(site: PortfolioSite): { lat: number; lng: number } | null {
  const location = site.location || site.address;

  if (!location) return null;

  // Handle GeoJSON Point format
  if (typeof location === 'object' && 'coordinates' in location) {
    const coords = (location as any).coordinates;
    if (Array.isArray(coords) && coords.length >= 2) {
      return { lat: coords[1], lng: coords[0] };
    }
  }

  // Handle string format "lat,lng"
  if (typeof location === 'string') {
    const parts = location.split(',').map(p => parseFloat(p.trim()));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parts[0], lng: parts[1] };
    }
  }

  return null;
}

/**
 * Calculate age in days from commissioning date
 */
function calculateAgeDays(codDate: string | null): number | null {
  if (!codDate) return null;

  const cod = new Date(codDate);
  if (isNaN(cod.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - cod.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Calculate age distribution buckets
 */
function calculateAgeDistribution(ageDays: number[]): AgeDistribution | null {
  const validAges = ageDays.filter(a => a !== null && a >= 0);
  if (validAges.length === 0) return null;

  const buckets: AgeBucket[] = [
    { label: '0-1 year', minDays: 0, maxDays: 365, count: 0 },
    { label: '1-3 years', minDays: 366, maxDays: 1095, count: 0 },
    { label: '3-5 years', minDays: 1096, maxDays: 1825, count: 0 },
    { label: '5-10 years', minDays: 1826, maxDays: 3650, count: 0 },
    { label: '10+ years', minDays: 3651, maxDays: Infinity, count: 0 },
  ];

  validAges.forEach(age => {
    const bucket = buckets.find(b => age >= b.minDays && age <= b.maxDays);
    if (bucket) bucket.count++;
  });

  return {
    average_age_days: Math.round(validAges.reduce((a, b) => a + b, 0) / validAges.length),
    min_age_days: Math.min(...validAges),
    max_age_days: Math.max(...validAges),
    buckets: buckets.filter(b => b.count > 0),
  };
}

/**
 * Extract manufacturer from entity
 */
function extractManufacturer(entity: Entity): string {
  // Check direct manufacturer field
  if (entity.manufacturer) return entity.manufacturer;

  // Check properties for manufacturer info
  const props = entity.properties || entity.metadata || {};
  if (props.manufacturer) return props.manufacturer;
  if (props.Manufacturer) return props.Manufacturer;
  if (props.make) return props.make;
  if (props.brand) return props.brand;

  // Try to extract from name or aliases
  const name = entity.name || entity.entity_name || '';
  const aliases = entity.aliases || [];

  // Common manufacturer patterns
  const manufacturers = [
    'SMA', 'Huawei', 'SolarEdge', 'Enphase', 'ABB', 'Schneider',
    'Trina', 'JinkoSolar', 'Canadian Solar', 'LONGi', 'JA Solar',
    'First Solar', 'Hanwha', 'Risen', 'Talesun',
  ];

  for (const mfg of manufacturers) {
    if (name.toLowerCase().includes(mfg.toLowerCase())) return mfg;
    if (aliases.some(a => a.toLowerCase().includes(mfg.toLowerCase()))) return mfg;
  }

  return 'Unknown';
}

/**
 * Extract model from entity
 */
function extractModel(entity: Entity): string {
  // Check direct model field
  if (entity.model) return entity.model;

  // Check properties for model info
  const props = entity.properties || entity.metadata || {};
  if (props.model) return props.model;
  if (props.Model) return props.Model;
  if (props.part_number) return props.part_number;

  // Try to use name as model if it's descriptive enough
  const name = entity.name || entity.entity_name || '';
  if (name && name.length > 3) {
    // Remove common prefixes like INV-, CB-, etc.
    const cleanName = name.replace(/^(INV|CB|CMB|MOD|STR|TRF)[-_]?/i, '');
    if (cleanName.length > 2) return cleanName;
  }

  return 'Unknown Model';
}

/**
 * Normalize model string for consistent grouping
 * Handles variations like:
 * - "SMA STP 60-US-10" vs "SMA Sunny Tripower 60"
 * - "INV-01" vs "INV-02" (same model, different instance)
 * - Case differences, spacing, punctuation
 */
function normalizeModel(model: string): string {
  let normalized = model
    // Convert to lowercase
    .toLowerCase()
    // Remove common instance suffixes like -01, -02, #1, #2, _A, _B
    .replace(/[-_#]\d+$/, '')
    .replace(/[-_][a-z]$/i, '')
    // Remove trailing numbers that look like instance IDs (but keep model numbers)
    .replace(/\s+\d{1,2}$/, '')
    // Normalize spacing
    .replace(/\s+/g, ' ')
    // Remove special characters except alphanumeric and spaces
    .replace(/[^a-z0-9\s]/g, '')
    // Trim
    .trim();

  // If we stripped too much, return original lowercase
  if (normalized.length < 3) {
    normalized = model.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  return normalized;
}

/**
 * Generate a normalized ID for component matching - GROUP BY MODEL ONLY
 * This groups components by their model number, regardless of manufacturer
 */
function generateNormalizedId(model: string, componentType: string): string {
  const normalizedModel = normalizeModel(model);
  const type = componentType.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${type}-${normalizedModel}`;
}

/**
 * Extract module information from string component's configuration
 * Module data is stored in configuration.aggregate_specs.module_properties
 */
interface ExtractedModuleInfo {
  manufacturer: string;
  model: string;
  stcPowerW: number | null;
  moduleCount: number;
}

function extractModuleInfoFromString(entity: Entity): ExtractedModuleInfo | null {
  const config = entity.configuration as Record<string, any> | undefined;
  if (!config) return null;

  const aggregateSpecs = config.aggregate_specs as Record<string, any> | undefined;
  if (!aggregateSpecs) return null;

  const moduleProps = aggregateSpecs.module_properties as Record<string, any> | undefined;
  if (!moduleProps) return null;

  const model = moduleProps.Model || moduleProps.model || '';
  const manufacturer = moduleProps.Manufacturer || moduleProps.manufacturer || '';

  // Need at least a model to count this
  if (!model) return null;

  return {
    manufacturer: manufacturer || 'Unknown',
    model,
    stcPowerW: moduleProps['STC Power (W)'] || moduleProps.stc_power_w || null,
    moduleCount: aggregateSpecs.module_count || 1,
  };
}

// ============================================================================
// Main Aggregation Function
// ============================================================================

/**
 * Aggregate component data from multiple sites into portfolio-level summary
 *
 * @param sitesData - Array of site data with entities
 * @returns Aggregated portfolio component data
 *
 * NOTE: When portfolio backend endpoints are available, replace this function's
 * internal logic to call the new endpoint directly instead of aggregating from sites.
 */
export function aggregatePortfolioComponents(
  sitesData: SelectedSiteData[]
): PortfolioComponentData {
  // Map to accumulate component summaries by category
  const categoryMap = new Map<string, {
    category: string;
    displayName: string;
    totalCount: number;
    siteIds: Set<string>;
    models: Map<string, {
      manufacturer: string;
      model: string;
      normalizedId: string;
      count: number;
      siteDistribution: Map<string, SiteComponentCount>;
      specifications: Record<string, any>;
      ageDays: number[];
    }>;
    ageDays: number[];
  }>();

  const allSiteIds = new Set<string>();
  let totalComponents = 0;

  // Process each site's entities
  for (const siteData of sitesData) {
    const { site, entities } = siteData;
    allSiteIds.add(site.site_id || site.id || '');

    const siteLocation = parseSiteLocation(site);
    const codDate = site.commissioning_date || null;
    const ageDays = calculateAgeDays(codDate);

    // Group entities by component type
    for (const entity of entities) {
      const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || 'unknown';
      const { category, displayName } = getCategoryInfo(componentType);

      // Initialize category if not exists
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          category,
          displayName,
          totalCount: 0,
          siteIds: new Set(),
          models: new Map(),
          ageDays: [],
        });
      }

      const categoryData = categoryMap.get(category)!;
      categoryData.totalCount++;
      categoryData.siteIds.add(site.site_id || site.id || '');
      if (ageDays !== null) categoryData.ageDays.push(ageDays);

      totalComponents++;

      // Extract manufacturer and model
      const manufacturer = extractManufacturer(entity);
      const model = extractModel(entity);
      const normalizedId = generateNormalizedId(model, componentType);

      // Initialize model if not exists
      if (!categoryData.models.has(normalizedId)) {
        categoryData.models.set(normalizedId, {
          manufacturer,
          model,
          normalizedId,
          count: 0,
          siteDistribution: new Map(),
          specifications: {},
          ageDays: [],
        });
      }

      const modelData = categoryData.models.get(normalizedId)!;
      modelData.count++;
      if (ageDays !== null) modelData.ageDays.push(ageDays);

      // Update specifications from entity properties
      const props = entity.properties || entity.metadata || {};
      if (Object.keys(props).length > 0) {
        Object.assign(modelData.specifications, props);
      }

      // Update site distribution
      const siteId = site.site_id || site.id || '';
      if (!modelData.siteDistribution.has(siteId)) {
        modelData.siteDistribution.set(siteId, {
          site_id: siteId,
          site_name: site.site_name || site.name || 'Unknown Site',
          site_location: siteLocation,
          count: 0,
          cod_date: codDate,
          component_age_days: ageDays,
        });
      }
      modelData.siteDistribution.get(siteId)!.count++;

      // SPECIAL HANDLING: Extract module info from string components
      // Modules are stored in string's configuration.aggregate_specs.module_properties
      if (componentType.toLowerCase() === 'string') {
        const moduleInfo = extractModuleInfoFromString(entity);
        if (moduleInfo) {
          const moduleCategory = 'module';
          const moduleDisplayName = 'PV Modules';

          // Initialize module category if not exists
          if (!categoryMap.has(moduleCategory)) {
            categoryMap.set(moduleCategory, {
              category: moduleCategory,
              displayName: moduleDisplayName,
              totalCount: 0,
              siteIds: new Set(),
              models: new Map(),
              ageDays: [],
            });
          }

          const moduleCategoryData = categoryMap.get(moduleCategory)!;
          moduleCategoryData.totalCount += moduleInfo.moduleCount;
          moduleCategoryData.siteIds.add(site.site_id || site.id || '');
          if (ageDays !== null) {
            // Add age for each module in the string
            for (let i = 0; i < moduleInfo.moduleCount; i++) {
              moduleCategoryData.ageDays.push(ageDays);
            }
          }

          totalComponents += moduleInfo.moduleCount;

          // Generate normalized ID for module model
          const moduleNormalizedId = generateNormalizedId(moduleInfo.model, 'module');

          // Initialize module model if not exists
          if (!moduleCategoryData.models.has(moduleNormalizedId)) {
            moduleCategoryData.models.set(moduleNormalizedId, {
              manufacturer: moduleInfo.manufacturer,
              model: moduleInfo.model,
              normalizedId: moduleNormalizedId,
              count: 0,
              siteDistribution: new Map(),
              specifications: moduleInfo.stcPowerW ? { 'STC Power (W)': moduleInfo.stcPowerW } : {},
              ageDays: [],
            });
          }

          const moduleModelData = moduleCategoryData.models.get(moduleNormalizedId)!;
          moduleModelData.count += moduleInfo.moduleCount;
          if (ageDays !== null) {
            for (let i = 0; i < moduleInfo.moduleCount; i++) {
              moduleModelData.ageDays.push(ageDays);
            }
          }

          // Update module specifications
          if (moduleInfo.stcPowerW) {
            moduleModelData.specifications['STC Power (W)'] = moduleInfo.stcPowerW;
          }

          // Update module site distribution
          if (!moduleModelData.siteDistribution.has(siteId)) {
            moduleModelData.siteDistribution.set(siteId, {
              site_id: siteId,
              site_name: site.site_name || site.name || 'Unknown Site',
              site_location: siteLocation,
              count: 0,
              cod_date: codDate,
              component_age_days: ageDays,
            });
          }
          moduleModelData.siteDistribution.get(siteId)!.count += moduleInfo.moduleCount;
        }
      }
    }
  }

  // Convert to output format
  const summaries: PortfolioComponentSummary[] = Array.from(categoryMap.values())
    .map(cat => {
      const models: ComponentModelSummary[] = Array.from(cat.models.values())
        .map(m => ({
          manufacturer: m.manufacturer,
          model: m.model,
          normalized_id: m.normalizedId,
          total_count: m.count,
          site_distribution: Array.from(m.siteDistribution.values())
            .sort((a, b) => b.count - a.count),
          specifications: m.specifications,
          average_age_years: m.ageDays.length > 0
            ? Math.round((m.ageDays.reduce((a, b) => a + b, 0) / m.ageDays.length / 365) * 10) / 10
            : null,
        }))
        .sort((a, b) => b.total_count - a.total_count);

      return {
        component_category: cat.category,
        display_name: cat.displayName,
        total_count: cat.totalCount,
        site_count: cat.siteIds.size,
        unique_model_count: models.length,
        models,
        age_distribution: calculateAgeDistribution(cat.ageDays),
        color: ComponentColors[cat.category] || ComponentColors.default,
      };
    })
    .sort((a, b) => {
      // Sort by priority, then by count
      const priorityA = CATEGORY_PRIORITY[a.component_category] || 0;
      const priorityB = CATEGORY_PRIORITY[b.component_category] || 0;
      if (priorityA !== priorityB) return priorityB - priorityA;
      return b.total_count - a.total_count;
    });

  return {
    summaries,
    total_components: totalComponents,
    total_sites: allSiteIds.size,
    last_updated: new Date().toISOString(),
  };
}

// ============================================================================
// Export for testing
// ============================================================================

export const __testing = {
  getCategoryInfo,
  parseSiteLocation,
  calculateAgeDays,
  calculateAgeDistribution,
  extractManufacturer,
  extractModel,
  normalizeModel,
  generateNormalizedId,
  extractModuleInfoFromString,
};
